import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getVerificationTrends, PatientManagementTrendsPayload } from '../../services';
import { translateLabelByTimeRange } from '../../utils';
import { ROLES } from '../../constants';

interface VerificationTrendItem {
  label: string;
  verified: number;
  unverified: number;
}

interface VerificationTrendsChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
  }[];
}

export const useVerificationTrends = (
  payload: PatientManagementTrendsPayload,
  selectedRange: string
) => {
  const { t } = useTranslation();
  const userRole = localStorage.getItem('userRole') || '';

  const query = useQuery<VerificationTrendItem[]>({
    queryKey: ['verification-trends', payload, selectedRange, userRole],
    queryFn: async () => {
      const result = await getVerificationTrends(payload);

      // Pick data based on role
      const trends: VerificationTrendItem[] =
        userRole === ROLES.PRACTISE_ADMIN &&
        result?.get_practise_admin_patient_management_verification_trend
          ? result.get_practise_admin_patient_management_verification_trend
          : result?.get_patient_management_verification_trend || [];

      return trends;
    },
    enabled: !!payload,
    staleTime: 5 * 60 * 1000,
    retry: 1
  });

  const translatedTrends = useMemo(() => {
    return (
      query.data?.map((item) => ({
        ...item,
        label: translateLabelByTimeRange(item.label, selectedRange, t)
      })) ?? []
    );
  }, [query.data, selectedRange, t]);

  const chartData: VerificationTrendsChartData = useMemo(() => {
    if (!translatedTrends.length) return { labels: [], datasets: [] };

    const labels = translatedTrends.map((item) => item.label);

    const datasets = [
      {
        label: t('patientsManagement.unverified'),
        data: translatedTrends.map((item) => item.unverified)
      },
      {
        label: t('patientsManagement.verified'),
        data: translatedTrends.map((item) => item.verified)
      }
    ];

    return { labels, datasets };
  }, [translatedTrends, t]);

  return {
    ...query,
    translatedTrends,
    chartData
  };
};
